/**
 * Stage S3 — document settings extraction.
 *
 * Ported from  (`latexLengthToCSS`) and :116-329
 * (`extractDocumentSettings`).
 *
 * Spec: docs/spec/00-MASTER-SPEC.md §3.3 (R-019…R-048) + §3.3.1 (the golden settings
 *       fixture, which `extractDocumentSettings(preamble)` must reproduce exactly);
 *       docs/spec/01-preamble-settings-macros-fonts.md §5.
 *
 * Pipeline position (§1): S1 `stripComments` → S2 `extractMacros` → **S3 here** → S4
 * metadata. The input is the WHOLE comment-stripped document, preamble *and* body
 * ( passes `cleanLatex`, not a preamble slice). Every `\setlength` in a
 * document body is therefore a document-wide setting.
 *
 * Matching discipline, and it is load-bearing everywhere below: every lookup except
 * the `\usepackage` font scan is a **non-global** `String.match`, so the FIRST
 * occurrence in the whole document wins and later redefinitions are ignored. That is
 * the opposite of LaTeX semantics (last assignment before use wins) and is why
 * `\setlength{\parindent}{0pt}` inside `\afficherEndNotes` (pre:230) never displaces
 * the `25pt` at pre:86.
 *
 * FIDELITY (CLAUDE.md §3): reproduces the source bug-for-bug. The `\geometry` parse
 * is deliberately broken (R-021) and its main result is then thrown away (R-033 /
 * RK-33 / RK-34, DF-11); `noHyphenation` (DF-12), `frenchColonSpace`/`frenchThinSpace`
 * (DF-13), `footnoteSymbols` and `useEndnotes` are extracted and never read. All of
 * that stays. The source's four `console.log` calls (, 302, 307, 326) are the
 * only thing dropped — the converter must be pure (CLAUDE.md § Code standards).
 */
import { extractBracedContent } from './braces'
import { detectFonts } from './fonts'
import type { DocumentSettings } from './types'

/**
 * R-019 — `latexLengthToCSS`,. The only unit conversion in the system.
 *
 * Order of the suffix tests is load-bearing: `pt` is tested before the bare-number
 * fallback, and the test is `endsWith`, not a full parse. So a rubber length whose
 * *tail* is a `pt` value — `"3pt plus3pt"` — takes the `pt` branch, `parseFloat`
 * reads only the leading `3`, and the result is `"4px"`. (Fragment 01 §5.5 predicts
 * `"3pt"` here; the oracle says `"4px"`. The oracle wins — see the module report.)
 *
 * `pt → px` uses 1pt = 96/72 px (TeX *big* point), not the true TeX point of
 * 1/72.27 in, and the raw JS float is emitted into the stylesheet:
 * `25pt → "33.33333333333333px"`, `2pt → "2.6666666666666665px"`, `3pt → "4px"`.
 *
 * Anything unrecognised falls through unchanged, so `\textwidth`, `2pc`, `5sp` etc.
 * are emitted verbatim and produce invalid CSS.
 */
export function latexLengthToCSS(length: string): string {
  const trimmed = length.trim()
  // Handle common LaTeX units —
  if (trimmed.endsWith('mm')) return trimmed
  if (trimmed.endsWith('cm')) return trimmed
  if (trimmed.endsWith('in')) return trimmed
  if (trimmed.endsWith('pt')) {
    const val = parseFloat(trimmed)
    return `${val / 72 * 96}px` // 72pt = 1in = 96px
  }
  if (trimmed.endsWith('em')) return trimmed
  if (trimmed.endsWith('ex')) return trimmed
  // Handle raw numbers (assume pt) —. NOTE: emitted AS pt, not converted.
  const num = parseFloat(trimmed)
  if (!isNaN(num)) return `${num}pt`
  return trimmed
}

/**
 * R-043 / R-044 — `getNumberingStyle`,.
 *
 * `counter` is `'footnote'` or `'endnote'`; the pattern is built by interpolation, so
 * it matches `\renewcommand{\thefootnote}{…}` / `\renewcommand{\theendnote}{…}`.
 *
 * Two consequences worth stating:
 *  - `([^}]+)` stops at the first `}`, so `{\arabic{endnote}}` captures
 *    `\arabic{endnote` — enough for the `includes` probes, but a literal marker
 *    containing braces is truncated.
 *  - the match is **non-global** (G-21): the first `\renewcommand` in file order wins.
 *    This used to decide the `\ifpandoc` branches too; since S1b resolves those before
 *    settings are read (2026-08-29) only one branch is present, so G-21 is now latent
 *    unless a document genuinely redefines the same marker twice.
 *
 * Probe order is fixed and first-wins: arabic → symbol → roman → Roman → alph → Alph
 * → literal. `includes` is case-sensitive, so `\Roman` correctly falls past the
 * `\roman` probe.
 */
export function getNumberingStyle(latex: string, counter: string): string | undefined {
  // String.raw in the source, to keep the backslashes readable.
  const pattern = String.raw`\\renewcommand\s*\{\\the${counter}\}\s*\{([^}]+)\}`
  const regex = new RegExp(pattern) // no `g`: first occurrence only
  const match = latex.match(regex)
  if (match) {
    const body = match[1] ?? '' // group 1 is mandatory on a match
    if (body.includes('\\arabic')) return 'arabic'
    if (body.includes('\\fnsymbol')) return 'symbol'
    if (body.includes('\\roman')) return 'roman'
    if (body.includes('\\Roman')) return 'Roman'
    if (body.includes('\\alph')) return 'alph'
    if (body.includes('\\Alph')) return 'Alph'
    // Literal marker fallback: strip leading `\`/`{` and trailing `\`/`}`, then trim.
    // `\renewcommand{\thefootnote}{*}` → "*"; `{\{*\}}` → captures `\{*\` → "*".
    return body.replace(/^[\\{]+|[\\}]+$/g, '').trim()
  }
  return undefined
}

/**
 * R-045 / R-046 — `getMarkerTemplate`,.
 *
 * `command` is `'makefnmark'` or `'makeenmark'`. Accepts both
 * `\renewcommand{\makeenmark}{…}` and `\renewcommand\makeenmark{…}`; a
 * `\newcommand{\makeenmark}{…}` is NOT picked up. Uses balanced-brace extraction
 * (unlike `getNumberingStyle`), so `\hspace{0.1em}\textsuperscript{\theenmark}`
 * survives intact.
 *
 * DEFECT, preserved — `exec` is called ONCE on a fresh `g`-flagged regex, so only the
 * first *syntactic* match is ever examined. The bare alternative `\\makeenmark` also
 * matches inside a longer control word, so a stray `\renewcommand\makeenmarkFoo`
 * earlier in the file consumes the single attempt, the char after it is not `{`, and
 * the real `\renewcommand{\makeenmark}{…}` further down is never seen. Verified
 * against the oracle. See the module report and settings.test.ts.
 */
export function getMarkerTemplate(latex: string, command: string): string | undefined {
  // String.raw in the source.
  const pattern = String.raw`\\renewcommand\s*(?:\{\\${command}\}|\\${command})\s*`
  const cmdPattern = new RegExp(pattern, 'g')
  const match = cmdPattern.exec(latex) // first occurrence only

  if (match) {
    // The definition starts immediately after the match; the trailing `\s*` has
    // already eaten any whitespace (including blank lines) before the brace.
    const startPos = match.index + match[0].length
    if (latex.charAt(startPos) === '{') {
      const content = extractBracedContent(latex, startPos)
      if (content) return content.content
    }
  }
  return undefined
}

/**
 * S3 — `extractDocumentSettings`,.
 *
 * Pure: takes the comment-stripped document, returns a fresh settings object.
 *
 * Key **insertion order** of the returned object is preserved statement-for-statement
 * from the source, because §3.3.1 is a byte-compare target for a JSON dump of it.
 * Fields the source assigns unconditionally (`footnoteStyle`, `endnoteStyle`,
 * `makefnmark`, `makeenmark`) are assigned here too, even when the value is
 * `undefined`, so `'makefnmark' in settings` also agrees.
 */
export function extractDocumentSettings(latex: string): DocumentSettings {
  // seed. Every other field stays undefined until set; the *defaults*
  // for unset fields are applied later, in generateCSS, not here.
  const settings: DocumentSettings = {
    fonts: detectFonts(latex), // R-020
  }

  // ── R-352 — \documentclass[…]{…}, the body size ──────────────────────────────
  // PREAMBLE LINK: NONE, deliberately — `preambuleV3.tex` carries no `\documentclass`. This one
  // comes from the DOCUMENT, which in all eight books is `\documentclass[12pt]{article}`.
  //
  // Added 2026-09-02 as the follow-up to the heading type scale. Before this, `\documentclass`
  // options were parsed by NOTHING (the closing note at the end of this file said so) and
  // `css.ts` hard-coded `12pt`, so a document asking for 11pt or 14pt was silently rendered at 12.
  // The whole heading scale hangs off this value now: `\Large` is 1.44 × body, `beforeskip` is
  // 1.51 × body, and so on.
  //
  // Only the size option is read. The class name and every other option (`twoside`, `a4paper`, …)
  // are still ignored, and the class name matters: LaTeX's size STEPS differ per class, which this
  // converter does not model. See docs/LaTeXDefaults.md.
  //
  // THREE CASES, and the middle one is the reason this is not a one-liner:
  //
  //   \documentclass[12pt]{article}  → '12pt'. What every book in the corpus says.
  //   \documentclass{article}        → '10pt'. LaTeX'S OWN DEFAULT for article/report/book. This
  //                                    is a real, compilable LaTeX document, so LaTeX's default is
  //                                    the right answer and inventing another would be exactly the
  //                                    mistake docs/LaTeXDefaults.md was written about.
  //   no \documentclass at all       → undefined, and css.ts keeps 12pt. Such input is NOT a LaTeX
  //                                    document — it would not compile — so there is no LaTeX
  //                                    default to be faithful to. It is the app's own partial
  //                                    preview (Mode B, an empty preamble box), and changing its
  //                                    size would move output for a case LaTeX says nothing about.
  const classMatch = latex.match(/\\documentclass\s*(?:\[([^\]]*)\])?\s*\{/)
  if (classMatch) {
    const sizeOption = (classMatch[1] ?? '')
      .split(',')
      .map((o) => o.trim())
      .find((o) => /^\d{1,2}pt$/.test(o))
    settings.bodyFontSize = sizeOption ?? '10pt'
  }

  // ── R-021 — \geometry{…} ─────────────────────────────────────────────────────────────
  // PREAMBLE LINK: \geometry{paper=a4paper,total={160mm,247mm},inner=25mm,top=25mm}
  // (preambuleV3.tex:107). READ AND HONOURED since 2026-09-07: `total`'s width drives
  // --latex-page-width and `top` drives --latex-margin-top. Keep the preamble line — it is the
  // only statement of the intended page layout that exists anywhere.
  //
  // WHAT WAS BROKEN, and it was a parsing bug rather than a policy. The capture used to be
  // `/\\geometry\s*\{([^}]+(?:\{[^}]*\}[^}]*)*)\}/`: the greedy `[^}]+` ran to the FIRST `}` — the
  // one closing `total={160mm,247mm}` — the optional group then matched zero times, `\}` succeeded
  // immediately, and the engine never backtracked into the alternation. The capture was only
  // `paper=a4paper,total={160mm,247mm`, so **every key written after a braced value was silently
  // dropped**: `inner=25mm` and `top=25mm` were never seen and the CSS fell back to 25mm/20mm.
  //
  // The fix is the one R-277 took on 2026-09-01, for the same class of bug on this same line:
  // count braces instead of guessing where the group ends. `extractBracedContent` is balanced to
  // any depth, so key order and nesting stop mattering. Still non-global — the FIRST \geometry in
  // the document wins, which is the source's rule.
  const geometryIndex = latex.search(/\\geometry\s*\{/)
  if (geometryIndex !== -1) {
    // `search` gives the index of the backslash; the group opens at the next `{`.
    const braceIndex = latex.indexOf('{', geometryIndex)
    // null when the group is never closed. Then nothing below matches and every value stays
    // undefined, which is what an unparseable \geometry should produce.
    const geomContent = extractBracedContent(latex, braceIndex)?.content ?? ''

    // R-022 — total={W,H}. Both values need an explicit mm|cm|in|pt unit.
    // pageWidth now reaches --latex-page-width (css.ts); pageHeight is still read by nothing,
    // because the web target has no page height and there is no @page rule (see css.ts).
    const totalMatch = geomContent.match(/total\s*=\s*\{?\s*(\d+(?:\.\d+)?(?:mm|cm|in|pt)),\s*(\d+(?:\.\d+)?(?:mm|cm|in|pt))\s*\}?/)
    if (totalMatch) {
      settings.pageWidth = totalMatch[1]
      settings.pageHeight = totalMatch[2]
    }

    // NOTE: geometry values are stored RAW — latexLengthToCSS is never applied here,
    // so `top=25pt` reaches the stylesheet as the literal `25pt`.
    const innerMatch = geomContent.match(/inner\s*=\s*(\d+(?:\.\d+)?(?:mm|cm|in|pt))/) // R-023
    if (innerMatch) settings.marginLeft = innerMatch[1]

    const outerMatch = geomContent.match(/outer\s*=\s*(\d+(?:\.\d+)?(?:mm|cm|in|pt))/) // R-024
    if (outerMatch) settings.marginRight = outerMatch[1]

    const topMatch = geomContent.match(/top\s*=\s*(\d+(?:\.\d+)?(?:mm|cm|in|pt))/) // R-025
    if (topMatch) settings.marginTop = topMatch[1]

    // R-026,  — extracted and never used: there is no @page rule anywhere.
    const bottomMatch = geomContent.match(/bottom\s*=\s*(\d+(?:\.\d+)?(?:mm|cm|in|pt))/)
    if (bottomMatch) settings.marginBottom = bottomMatch[1]

    // R-027/R-028,  — `left`/`right` run AFTER `inner`/`outer` and override
    // them, regardless of which appeared first in the option list.
    const leftMatch = geomContent.match(/left\s*=\s*(\d+(?:\.\d+)?(?:mm|cm|in|pt))/)
    if (leftMatch) settings.marginLeft = leftMatch[1]

    const rightMatch = geomContent.match(/right\s*=\s*(\d+(?:\.\d+)?(?:mm|cm|in|pt))/)
    if (rightMatch) settings.marginRight = rightMatch[1]
  }

  // ── R-029 — \setlength{\parskip}{…},  ───────────────────────────────
  // PREAMBLE LINK: \setlength{\parskip}{3pt plus 3pt minus 2pt} (preambuleV3.tex:123) — the space
  // between two paragraphs. READ AND HONOURED, via --latex-par-skip → `p { margin }` in css.ts.
  // The next three rules are the same story: \parindent (:158), \parsep (:131) and \linespread
  // (:136) are all read here and all reach the stylesheet.
  // Rubber length: keep the base value only. The split pattern requires whitespace
  // before the keyword, so `3pt plus3pt` is NOT split — and then survives anyway,
  // because latexLengthToCSS's `endsWith('pt')` test fires on the tail. Real
  // preamble: `3pt plus 3pt minus 2pt` (pre:72) → `4px`.
  const parskipMatch = latex.match(/\\setlength\s*\{\\parskip\}\s*\{([^}]+)\}/)
  if (parskipMatch) {
    const baseValue = (parskipMatch[1] ?? '').split(/\s+plus|\s+minus/)[0]?.trim() ?? ''
    settings.parSkip = latexLengthToCSS(baseValue)
  }

  // R-030 — \setlength{\parindent}{…},. NO plus/minus stripping here (the
  // asymmetry with parskip/parsep is in the source). `25pt` (pre:86) → the raw JS
  // float `33.33333333333333px`.
  const parindentMatch = latex.match(/\\setlength\s*\{\\parindent\}\s*\{([^}]+)\}/)
  if (parindentMatch) {
    settings.parIndent = latexLengthToCSS(parindentMatch[1] ?? '')
  }

  // R-031 — \setlength{\parsep}{…},. List item spacing. Split as parskip.
  // Real preamble: `2pt plus 1pt minus 1pt` (pre:75) → `2.6666666666666665px`.
  const parsepMatch = latex.match(/\\setlength\s*\{\\parsep\}\s*\{([^}]+)\}/)
  if (parsepMatch) {
    const baseValue = (parsepMatch[1] ?? '').split(/\s+plus|\s+minus/)[0]?.trim() ?? ''
    settings.parSep = latexLengthToCSS(baseValue)
  }

  // R-032 — \linespread{…},. CSS uses lineSpread * 1.2, so 1.1
  // → 1.32. `parseFloat` of a non-numeric body yields NaN, which is stored as NaN and
  // is falsy downstream, giving the 1.6 default. Reproduced deliberately.
  const linespreadMatch = latex.match(/\\linespread\s*\{([^}]+)\}/)
  if (linespreadMatch) {
    settings.lineSpread = parseFloat(linespreadMatch[1] ?? '')
  }

  // R-033 — \raggedright presence,. A plain substring test over the whole
  // document: one `\raggedright` inside a table cell flips the global setting.
  // PREAMBLE LINK: \sloppy (preambuleV3.tex:184) is NOT what this reads — \raggedright is a
  // `\sloppy` (pre:98) is detected by nothing. The resulting `--latex-text-align` is
  // then overridden for paragraphs by `text-align: justify !important`.
  // computed here reaches only --latex-text-align, whose sole consumer is `.extract`, which
  // nothing emits — so this rule currently affects no output at all.
  if (latex.includes('\\raggedright')) {
    settings.textAlign = 'left'
  }

  // R-034 — \usepackage[lang]{babel},. Whole option list, lower-cased, not
  // split: `[french,english]` → `"french,english"`. `\usepackage{babel}` with no
  // options is not detected. Never read again — both HTML templates hard-code
  // PREAMBLE LINK: \usepackage[french]{babel} (preambuleV3.tex:19). PARSED HERE, THEN NEVER READ
  // `lang="en"` (G-26 / DF-19).
  // interpolating settings.language there, not changing anything in this function.
  const babelMatch = latex.match(/\\usepackage\[([^\]]+)\]\{babel\}/)
  if (babelMatch) {
    settings.language = (babelMatch[1] ?? '').toLowerCase()
  }

  // PREAMBLE LINK: \FBsetspaces{colon}{0.5}{0.1}{0.1} and \FBsetspaces{thin}{0.5}{0.1}{0.1}
  // (preambuleV3.tex:142-143) — the French space before `:` and before `; ! ?`. PARSED HERE, THEN
  // R-035/R-036 — \FBsetspaces{colon|thin}{…},. Only the FIRST of the
  // ever references them (RK-38). The spacing readers actually see comes from the `~` characters
  // translators type by hand, which R-222 turns into &nbsp;. Honouring these two lines needs a new
  // converter pass that wraps the punctuation, plus a real rule in css.ts (DF-13).
  // R-035/R-036 — \FBsetspaces{colon|thin}{…}. Only the FIRST of the
  // three `{}{}{}` numeric arguments is captured; stretch and shrink are discarded.
  // generateCSS turns `"0.5"` into `0.25em` and then no rule uses it
  // (DF-13). \FBsetspaces itself is never removed from the body either (RK-20), so it
  // leaks as literal text when written after \begin{document}.
  const colonSpaceMatch = latex.match(/\\FBsetspaces\s*\{colon\}\s*\{([^}]+)\}/)
  if (colonSpaceMatch) {
    settings.frenchColonSpace = colonSpaceMatch[1]
  }
  const thinSpaceMatch = latex.match(/\\FBsetspaces\s*\{thin\}\s*\{([^}]+)\}/)
  if (thinSpaceMatch) {
    settings.frenchThinSpace = thinSpaceMatch[1]
  }

  // R-037 — \setlength{\marginparwidth}{…},. THIS IS THE VERSE-NUMBER
  // COLUMN WIDTH (pre:91-95). Real preamble `6mm`; default `20mm`.
  // PREAMBLE LINK: \setlength{\marginparwidth}{6mm} (preambuleV3.tex:174) — "à 6 mm du bord".
  // READ AND HONOURED, via --latex-marginpar-width → .marginpar in css.ts. It governs \Nverset,
  // \Npar and \Nhidden, which the preamble builds on \marginpar. Its companion on preamble
  // line 128, \reversemarginpar, is NOT read — the left placement is hard-coded in css.ts.
  // The next rule, \leftmargini (:166), is likewise read and honoured (blockquote margins).
  const marginparwidthMatch = latex.match(/\\setlength\s*\{\\marginparwidth\}\s*\{([^}]+)\}/)
  if (marginparwidthMatch) {
    settings.marginParWidth = latexLengthToCSS(marginparwidthMatch[1] ?? '')
  }

  // R-038 — \setlength{\leftmargini}{…},. Drives blockquote margins.
  // Default = parIndent.
  const leftmarginiMatch = latex.match(/\\setlength\s*\{\\leftmargini\}\s*\{([^}]+)\}/)
  if (leftmarginiMatch) {
    settings.leftMarginQuote = latexLengthToCSS(leftmarginiMatch[1] ?? '')
  }

  // PREAMBLE LINK: \setcounter{tocdepth}{4} (preambuleV3.tex:201) — "Présenter quatre niveaux de
  // titre". READ AND HONOURED: this is the only parse of the counter, and index.ts S17 passes the
  // result to toc.ts as its recursion bound.
  //
  // R-039 — \setcounter{tocdepth}{n}. Every value a document may legally write is recognised.
  // `-?\d+` since 2026-09-07: it used to be `\d+`, so `{-1}` did not match AT ALL, tocDepth stayed
  // undefined, and index.ts's fallback turned "show nothing" into four levels. Same wrong answer
  // as the `{0}` bug fixed in the previous commit, reached by a different route — that one was a
  // falsy check downstream, this one is a value the parser never delivered.
  // `article` has no \part or \chapter, so any depth at or below 0 yields an empty table of
  // contents, which toc.ts's `currentDepth > tocDepth` guard already rendered correctly.
  const tocdepthMatch = latex.match(/\\setcounter\s*\{tocdepth\}\s*\{(-?\d+)\}/)
  if (tocdepthMatch) {
    // parseInt with no radix; the capture is `-?\d+`, so the base is never ambiguous.
    settings.tocDepth = parseInt(tocdepthMatch[1] ?? '')
  }

  // R-040 — \fnsymbol{footnote} presence,. Literal substring tests.
  // Extracted and never read. In the real preamble the `\fnsymbol` lines (pre:234-236)
  // are comments and are already blanked by S1, so this stays undefined.
  if (latex.includes('\\fnsymbol{footnote}') || latex.includes('\\renewcommand{\\thefootnote}{\\fnsymbol{footnote}}')) {
    settings.footnoteSymbols = true
  }

  // R-041 — \usepackage[none]{hyphenat},. Whitespace-sensitive literal
  // test: `[none ]` or `[ none ]` does not match. IGNORED downstream —
  // PREAMBLE LINK: \usepackage[none]{hyphenat} (preambuleV3.tex:24) — "Refuser la césure des
  // emits `hyphens: auto` unconditionally, contradicting pre:20-21 "Refuser la
  // césure". DF-12, kept.
  // is in css.ts's `p` rule, not here. Compounded by templates.ts declaring `lang="en"`, which is
  // the dictionary the browser hyphenates a French text with.
  if (latex.includes('\\usepackage[none]{hyphenat}')) {
    settings.noHyphenation = true
  }

  // R-042 — endnotes detection,. Either the package or the exact
  // `\newcommand{\note}[1]{\endnote{#1}}` spelling (pre:229 modulo its leading TAB —
  // the substring test ignores what comes before). Extracted and never read.
  // PREAMBLE LINK: \usepackage{endnotes} (preambuleV3.tex:40) — "Gérer des notes de fin de
  // document". PARSED HERE, THEN NEVER READ. The note machinery in notes.ts is entirely
  // hard-coded and does not consult this flag. The block it builds now does reach the page
  // (DF-1 fixed 2026-08-29 by S1b), but not because of this field — nothing consults it.
  // If this ever becomes load-bearing, update the CONVERTISSEUR note on preambuleV3.tex:40.
  if (latex.includes('\\usepackage{endnotes}') || latex.includes('\\newcommand{\\note}[1]{\\endnote{#1}}')) {
    settings.useEndnotes = true
  }

  // R-043/R-044/R-045/R-046 —. Assigned unconditionally, so the keys exist
  // (with value undefined) even when nothing matched.
  // PREAMBLE LINK: \renewcommand{\thefootnote}{\arabic{footnote}} (preambuleV3.tex:485) and
  // \renewcommand{\theendnote}{\arabic{endnote}} (:490). READ AND HONOURED — notes.ts feeds these
  // to getNoteMarker, so endnotes really are numbered in arabic digits as asked.
  // /!\ The two lines sit in OPPOSITE branches of \ifpandoc, and S1b keeps only the \else
  // (XeLaTeX) one. So \theendnote is read and \thefootnote is NOT — it does not exist in that
  // branch, because that branch routes \note to \endnote and uses no footnotes. Consequence,
  // deliberate and recorded in DF-1: a document writing \footnote{} DIRECTLY now falls back to
  // notes.ts's 'symbol' default (*, †, ‡) instead of arabic. That matches the preamble's own
  // guidance for the one document known to mix both kinds — its commented block at :510-513
  // prescribes \fnsymbol{footnote} for exactly that case.
  // PREAMBLE LINK: \renewcommand{\makeenmark}{\hspace{0.1em}\textsuperscript{\theenmark}} (:465).
  // PARTLY READ: getMarkerTemplate captures the whole definition, R-047 below pulls the \hspace
  // out of it, and notes.ts matches the \textsuperscript shape — but the superscript itself is
  // hard-coded there, not derived from this line. \makefnmark is not defined in this preamble.
  settings.footnoteStyle = getNumberingStyle(latex, 'footnote')
  settings.endnoteStyle = getNumberingStyle(latex, 'endnote')
  settings.makefnmark = getMarkerTemplate(latex, 'makefnmark')
  settings.makeenmark = getMarkerTemplate(latex, 'makeenmark')

  // R-047/R-048 — derived marker spacing,. First `\hspace{…}` in the
  // template, wherever it sits. `\hspace*` is NOT matched (no `\*?` in the pattern).
  // Real preamble: makeenmark `\hspace{0.1em}…` (:465) → endnoteSpacing `0.1em`
  // → `.endnote-num { margin-left: 0.1em }`. Live since the DF-1 fix (2026-08-29): the notes
  // block now reaches the page, so this spacing is finally visible to a reader.
  if (settings.makefnmark) {
    const spaceMatch = settings.makefnmark.match(/\\hspace\{([^}]+)\}/)
    if (spaceMatch) settings.footnoteSpacing = latexLengthToCSS(spaceMatch[1] ?? '')
  }
  if (settings.makeenmark) {
    const spaceMatch = settings.makeenmark.match(/\\hspace\{([^}]+)\}/)
    if (spaceMatch) settings.endnoteSpacing = latexLengthToCSS(spaceMatch[1] ?? '')
  }

  // PREAMBLE LINK — WHAT THIS FUNCTION DELIBERATELY DOES NOT PARSE. Each line below exists in
  // preambuleV3.tex and carries a matching `% CONVERTISSEUR:` note; the code that produces the
  // behaviour anyway (where there is any) carries its own PREAMBLE LINK note. Line numbers are
  // preambuleV3.tex's. Keep both ends in step (CLAUDE.md, "Preamble and converter are documented
  // NOT EXTRACTED, deliberately (fragment 01 §5.15):
  //   \reversemarginpar (pre:95)   — no rule at all; deleted from the body at.
  //                                  Margin notes are floated left by hard-coded CSS,
  //                                  which is why verse numbers land correctly. Do not
  //                                  "implement" it — left placement is an editorial
  //                                  requirement (pre:62, 91-95) already satisfied.
  //   \renewcommand{\enoteformat}  (pre:218) — the note-list format is hard-coded at
  //                                   and happens to match `\theenmark.\enspace`.
  //   \usepackage[stable,bottom]{footmisc}, \usepackage[all]{nowidow}, \sloppy,
  //   \headrulewidth — none are parsed.
  //
  //   /!\ CORRECTED 2026-09-02: this list used to say "\documentclass options" too. The SIZE
  //   option is now parsed (R-352 at the top of this function) and drives --latex-body-size and
  //   with it the whole heading type scale. Everything else about \documentclass — the class name,
  //   `twoside`, `a4paper`, … — is still ignored, and the class name matters: LaTeX's size steps
  //   are a per-class lookup table this converter does not model (docs/LaTeXDefaults.md).
  //
  // PREAMBLE LINK — every line above exists in preambuleV3.tex and carries a CONVERTISSEUR note
  // saying so. Keep the two in step: if any of these becomes parsed, or if a new preamble line
  // is added that this function ignores, update both ends. See CLAUDE.md, "Preamble and converter
  // are documented both ways".
  //                                         that the hard-coded rule already satisfies.
  //     \renewcommand{\enoteformat} (:475) → the "1." + &ensp; shape in notes.ts, which happens to
  //                                         match what this line asks for.
  //     \usepackage[all]{nowidow} (:29)   → widows/orphans in css.ts.
  //     \usepackage{longtable} (:69)      → the longtable handler in environments.ts.
  //     \usepackage{pdflscape} (:75)      → the landscape handler in environments.ts (screen only).
  //     \usepackage{enumitem} (:57)       → the itemize/enumerate handlers in environments.ts.
  //     \addto\captionsfrench{\renewcommand{\contentsname}{}} (:238) → toc.ts emits no TOC title.
  //     \sloppy (:184)                    → needs no equivalent; browsers always stretch spaces on
  //                                         a justified line. Justification itself is hard-coded.
  //
  // Also extracted here and then read by NOBODY (verified by inspecting every hit, not by match
  // counts): settings.pageWidth (overridden by the 172mm constant in css.ts), settings.pageHeight,
  // settings.marginBottom, settings.language (templates hard-code lang="en"),
  // settings.noHyphenation (css.ts emits `hyphens: auto` regardless), settings.useEndnotes,
  // settings.footnoteSymbols. And settings.frenchColonSpace / frenchThinSpace reach the stylesheet
  // as custom properties that no rule ever references (RK-38).
  return settings
  //     pageWidth (overridden by the 172mm constant in css.ts), pageHeight, marginBottom,
  //     language, noHyphenation, useEndnotes, footnoteSymbols. frenchColonSpace/frenchThinSpace
  //     do reach the stylesheet, as custom properties no rule references (RK-38).
  return settings
}
